import * as R from 'ramda';
import React, {
  memo,
  useRef,
  useState,
  useCallback,
} from 'react';
// helpers
import * as G from '../../helpers';
// icons
import * as I from '../../svgs';
// ui
import { Iframe, AbsoluteBox } from '../../ui';
// component document-preview
import { ImageUI, ImageViewerWrapper } from './ui';
//////////////////////////////////////////////////

const iconColor = G.getTheme('colors.dark.blue');

const ImagePreview = memo(({ src }: Object) => {
  const [scale, setScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const lastMousePosition = useRef({ x: 0, y: 0 });

  const handleWheel = useCallback((event: Event) => {
    event.preventDefault();

    const delta = event.deltaY > 0 ? -0.1 : 0.1;

    setScale((prevScale: number) => Math.min(Math.max(prevScale + delta, 0.5), 5));
  }, []);

  const handleMouseDown = useCallback((event: Event) => {
    setIsPanning(true);

    if (event.button === 0) {
      lastMousePosition.current = { x: event.clientX, y: event.clientY };
    }

    if (event.button === 1) {
      setScale(1);
      setIsPanning(false);
      setPosition({ x: 0, y: 0 });
    }
  }, [lastMousePosition]);

  const handleMouseMove = useCallback((event: Event) => {
    if (!isPanning) return;

    const dx = event.clientX - lastMousePosition.current.x;
    const dy = event.clientY - lastMousePosition.current.y;

    lastMousePosition.current = { x: event.clientX, y: event.clientY };

    if (R.and(imageRef.current, containerRef.current)) {
      const container = containerRef.current.getBoundingClientRect();
      const image = imageRef.current.getBoundingClientRect();

      const containerWidth = container.width;
      const containerHeight = container.height;

      const scaledImageWidth = image.width * scale;
      const scaledImageHeight = image.height * scale;

      const minX = Math.min(0, (containerWidth - scaledImageWidth) / 2);
      const maxX = Math.max(0, (scaledImageWidth - containerWidth) / 2);

      const minY = Math.min(0, (containerHeight - scaledImageHeight) / 2);
      const maxY = Math.max(0, (scaledImageHeight - containerHeight) / 2);

      setPosition((prevPosition: number) => ({
        x: Math.min(Math.max(prevPosition.x + dx, -maxX), -minX),
        y: Math.min(Math.max(prevPosition.y + dy, -maxY), -minY),
      }));
    }
  }, [imageRef, isPanning, containerRef, lastMousePosition]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const enterFullscreen = useCallback((event: Event) => {
    event.preventDefault();

    const element = imageRef.current;

    if (element) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    }
  }, [imageRef]);

  return (
    <ImageViewerWrapper
      ref={containerRef}
      isPanning={isPanning}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={(event: Event) => handleWheel(event)}
      onMouseDown={(event: Event) => handleMouseDown(event)}
      onMouseMove={(event: Event) => handleMouseMove(event)}
    >
      <ImageUI
        src={src}
        alt='Preview'
        scale={scale}
        ref={imageRef}
        position={position}
      />
      <AbsoluteBox top={10} right={10} cursor='pointer' onClick={enterFullscreen}>
        {I.fullScreen(iconColor, 32, 32)}
      </AbsoluteBox>
    </ImageViewerWrapper>
  );
});

const Preview = ({ doc }: Object) => {
  const src = R.path(['preview'], doc);
  const type = R.path(['file', 'type'], doc);

  if (type.match(/image.*/)) {
    return <ImagePreview src={src} />;
  }

  return <Iframe type={type} src={src} />;
};

export default Preview;
